import { create } from "zustand";
import type { Document, DocumentListItem, Project, ProjectListItem } from "../types/document";
import type { CompileResponse, CompileError } from "../types/compilation";
import { api } from "../lib/api";
import { useUIStore } from "./ui";

type SaveEventCallback = (event: "save_failed" | "save_recovered") => void;

interface PendingEdit {
  proposedContent: string;
  description?: string;
}

interface DocumentState {
  projects: ProjectListItem[];
  currentProject: Project | null;
  documents: DocumentListItem[];
  currentDocument: Document | null;
  isLoading: boolean;
  isSaving: boolean;
  isCompiling: boolean;
  isAutoCompiling: boolean;
  autoCompile: boolean;
  compileResult: CompileResponse | null;
  errors: CompileError[];
  unsavedChanges: boolean;
  saveFailed: boolean;
  fetchError: string | null;

  pendingEdit: PendingEdit | null;

  fetchProjects: () => Promise<void>;
  createProject: (name: string, description?: string) => Promise<Project>;
  deleteProject: (projectId: string) => Promise<void>;
  renameProject: (projectId: string, name: string) => Promise<void>;
  setCurrentProject: (project: Project | null) => void;
  fetchDocuments: (projectId: string) => Promise<void>;
  createDocument: (projectId: string, filename: string, docType: "latex" | "markdown" | "docx", templateId?: string) => Promise<Document>;
  openDocument: (documentId: string) => Promise<void>;
  updateContent: (content: string) => void;
  saveDocument: () => Promise<void>;
  compile: () => Promise<void>;
  deleteDocument: (documentId: string) => Promise<void>;
  setAutoCompile: (enabled: boolean) => void;
  onSaveEvent: (cb: SaveEventCallback) => () => void;
  proposePendingEdit: (proposedContent: string, description?: string) => void;
  acceptPendingEdit: () => void;
  rejectPendingEdit: () => void;
}

let _projectsFetchPromise: Promise<void> | null = null;
let _autoCompileTimer: ReturnType<typeof setTimeout> | null = null;
let _latestFetchProjectId: string | null = null;
let _latestOpenDocumentId: string | null = null;
const AUTO_COMPILE_DEBOUNCE_MS = 2000;
const SAVE_MAX_RETRIES = 3;
const SAVE_RETRY_BASE_MS = 1000;
const _saveEventListeners = new Set<SaveEventCallback>();

export const useDocumentStore = create<DocumentState>()((set, get) => ({
  projects: [],
  currentProject: null,
  documents: [],
  currentDocument: null,
  isLoading: false,
  isSaving: false,
  isCompiling: false,
  isAutoCompiling: false,
  autoCompile: true,
  compileResult: null,
  errors: [],
  unsavedChanges: false,
  saveFailed: false,
  fetchError: null,
  pendingEdit: null,

  fetchProjects: async () => {
    if (_projectsFetchPromise) return _projectsFetchPromise;
    set({ isLoading: true, fetchError: null });
    _projectsFetchPromise = (async () => {
      try {
        const projects = await api.get<ProjectListItem[]>("/api/v1/projects");
        set({ projects });
      } catch {
        set({ fetchError: "Could not connect to the server. Make sure the backend is running." });
      } finally {
        set({ isLoading: false });
        _projectsFetchPromise = null;
      }
    })();
    return _projectsFetchPromise;
  },

  createProject: async (name, description) => {
    try {
      const project = await api.post<Project>("/api/v1/projects", { name, description });
      set((s) => ({ projects: [{ ...project }, ...s.projects] }));
      return project;
    } catch (err) {
      set({ fetchError: "Failed to create project." });
      throw err;
    }
  },

  deleteProject: async (projectId) => {
    try {
      await api.delete(`/api/v1/projects/${projectId}`);
      set((s) => ({
        projects: s.projects.filter((p) => p.id !== projectId),
        currentProject: s.currentProject?.id === projectId ? null : s.currentProject,
      }));
    } catch (err) {
      set({ fetchError: "Failed to delete project." });
      throw err;
    }
  },

  renameProject: async (projectId, name) => {
    try {
      await api.put(`/api/v1/projects/${projectId}`, { name });
      set((s) => ({
        projects: s.projects.map((p) => (p.id === projectId ? { ...p, name } : p)),
        currentProject: s.currentProject?.id === projectId ? { ...s.currentProject, name } : s.currentProject,
      }));
    } catch (err) {
      set({ fetchError: "Failed to rename project." });
      throw err;
    }
  },

  setCurrentProject: (project) => set({ currentProject: project }),

  fetchDocuments: async (projectId) => {
    _latestFetchProjectId = projectId;
    set({ isLoading: true, fetchError: null });
    try {
      const documents = await api.get<DocumentListItem[]>(
        `/api/v1/projects/${projectId}/documents`,
      );
      if (_latestFetchProjectId !== projectId) return;
      const current = get().currentDocument;
      const currentStale = current && !documents.some((d) => d.id === current.id);
      set({ documents, ...(currentStale ? { currentDocument: null } : {}) });
    } catch (err) {
      if (_latestFetchProjectId !== projectId) return;
      const msg = err instanceof Error ? err.message : "Could not load documents";
      set({ fetchError: msg });
      throw err;
    } finally {
      if (_latestFetchProjectId === projectId) {
        set({ isLoading: false });
      }
    }
  },

  createDocument: async (projectId, filename, docType, templateId) => {
    const defaultContent = (dt: string) => {
      if (dt === "latex") return "\\documentclass{article}\n\\usepackage[utf8]{inputenc}\n\n\\title{Untitled}\n\\author{}\n\\date{}\n\n\\begin{document}\n\\maketitle\n\n\n\n\\end{document}\n";
      if (dt === "docx") return "<h1>Untitled Document</h1><p></p>";
      return "# Untitled\n\n";
    };
    let content: string;
    if (templateId) {
      try {
        const tmpl = await api.get<{ content: string }>(`/api/v1/templates/${templateId}`);
        content = tmpl.content;
      } catch {
        content = defaultContent(docType);
      }
    } else {
      content = defaultContent(docType);
    }
    const doc = await api.post<Document>("/api/v1/documents", {
      project_id: projectId,
      filename,
      doc_type: docType,
      content,
    });
    set((s) => ({
      documents: [...s.documents, { id: doc.id, filename: doc.filename, doc_type: doc.doc_type, parent_path: doc.parent_path, sort_order: doc.sort_order, updated_at: doc.updated_at }],
    }));
    return doc;
  },

  openDocument: async (documentId) => {
    _latestOpenDocumentId = documentId;
    set({ isLoading: true, fetchError: null });
    try {
      const doc = await api.get<Document>(`/api/v1/documents/${documentId}`);
      if (_latestOpenDocumentId !== documentId) return;
      set({ currentDocument: doc, errors: [], compileResult: null, unsavedChanges: false });
    } catch (err) {
      if (_latestOpenDocumentId !== documentId) return;
      const msg = err instanceof Error ? err.message : "Could not open document";
      set({ fetchError: msg });
    } finally {
      if (_latestOpenDocumentId === documentId) {
        set({ isLoading: false });
      }
    }
  },

  updateContent: (content) => {
    set((s) => {
      if (!s.currentDocument) return s;
      return {
        currentDocument: { ...s.currentDocument, content },
        unsavedChanges: true,
      };
    });

    const { autoCompile, currentDocument } = get();
    if (autoCompile && currentDocument?.doc_type === "latex") {
      const docIdAtSchedule = currentDocument.id;
      if (_autoCompileTimer) clearTimeout(_autoCompileTimer);
      _autoCompileTimer = setTimeout(() => {
        _autoCompileTimer = null;
        const { isCompiling, currentDocument: docNow } = get();
        if (!isCompiling && docNow?.id === docIdAtSchedule) {
          set({ isAutoCompiling: true });
          get().compile().finally(() => set({ isAutoCompiling: false }));
        }
      }, AUTO_COMPILE_DEBOUNCE_MS);
    }
  },

  saveDocument: async () => {
    const doc = get().currentDocument;
    if (!doc) return;
    const docId = doc.id;

    set({ isSaving: true });

    for (let attempt = 0; attempt < SAVE_MAX_RETRIES; attempt++) {
      const current = get().currentDocument;
      if (!current || current.id !== docId) {
        set({ isSaving: false });
        return;
      }
      try {
        const saved = await api.put<Document>(`/api/v1/documents/${docId}`, {
          content: current.content,
          expected_updated_at: current.updated_at,
        });

        if (get().saveFailed) {
          _saveEventListeners.forEach((cb) => cb("save_recovered"));
        }
        set((s) => ({
          unsavedChanges: false,
          saveFailed: false,
          isSaving: false,
          currentDocument: s.currentDocument?.id === docId && saved?.updated_at
            ? { ...s.currentDocument, updated_at: saved.updated_at }
            : s.currentDocument,
        }));
        return;
      } catch (err) {
        const status = (err as { status?: number })?.status;
        if (status === 409) {
          set({
            saveFailed: true,
            errors: [{ line: null, message: "Document was modified elsewhere. Refresh to get the latest version.", severity: "error", file: null }],
            isSaving: false,
          });
          _saveEventListeners.forEach((cb) => cb("save_failed"));
          return;
        }
        if (attempt < SAVE_MAX_RETRIES - 1) {
          await new Promise((r) => setTimeout(r, SAVE_RETRY_BASE_MS * 2 ** attempt));
        }
      }
    }

    set({
      saveFailed: true,
      errors: [{ line: null, message: "Could not save — check your connection", severity: "error", file: null }],
      isSaving: false,
    });
    _saveEventListeners.forEach((cb) => cb("save_failed"));
    return;
  },

  compile: async () => {
    const { currentDocument } = get();
    if (!currentDocument || currentDocument.doc_type !== "latex") return;

    set({ isCompiling: true, errors: [] });
    try {
      const result = await api.post<CompileResponse>("/api/v1/compile", {
        source: currentDocument.content,
        engine: "pdflatex",
      });
      const allErrors = [...result.errors, ...(result.warnings || [])];
      set({
        compileResult: result,
        errors: allErrors,
      });
      if (!result.success && allErrors.some((e) => e.severity === "error")) {
        useUIStore.getState().openBottomPanelTab("errors");
      }
    } catch {
      set({
        errors: [{ line: null, message: "Compilation request failed", severity: "error", file: null }],
      });
      useUIStore.getState().openBottomPanelTab("errors");
    } finally {
      set({ isCompiling: false });
    }
  },

  deleteDocument: async (documentId) => {
    try {
      await api.delete(`/api/v1/documents/${documentId}`);
      set((s) => ({
        documents: s.documents.filter((d) => d.id !== documentId),
        currentDocument: s.currentDocument?.id === documentId ? null : s.currentDocument,
      }));
    } catch (err) {
      set({ fetchError: "Failed to delete document." });
      throw err;
    }
  },

  setAutoCompile: (enabled) => {
    if (!enabled && _autoCompileTimer) {
      clearTimeout(_autoCompileTimer);
      _autoCompileTimer = null;
    }
    set({ autoCompile: enabled });
  },

  onSaveEvent: (cb) => {
    _saveEventListeners.add(cb);
    return () => { _saveEventListeners.delete(cb); };
  },

  proposePendingEdit: (proposedContent, description) => {
    set({ pendingEdit: { proposedContent, description } });
  },

  acceptPendingEdit: () => {
    const { pendingEdit } = get();
    if (!pendingEdit) return;
    get().updateContent(pendingEdit.proposedContent);
    set({ pendingEdit: null });
  },

  rejectPendingEdit: () => {
    set({ pendingEdit: null });
  },
}));
